import { useState, type FormEvent } from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const MAX_NAME_LENGTH = 120;

interface ListTitleProps {
    name: string;
    saving: boolean;
    /** Resolves when the new name is saved; rejects to keep the editor open. */
    onRename: (name: string) => Promise<unknown>;
}

/**
 * The list's name as the page heading, with a pencil to rename it in place
 * (#376). Lists from "Shop for your plan" are named for their days; this is
 * how a client calls one "Braai weekend" instead.
 */
export function ListTitle({ name, saving, onRename }: ListTitleProps) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState(name);
    const [error, setError] = useState<string | null>(null);

    const startEditing = () => {
        setDraft(name);
        setError(null);
        setEditing(true);
    };

    const submit = (event: FormEvent) => {
        event.preventDefault();
        const trimmed = draft.trim();
        if (!trimmed) {
            setError('Give the list a name.');
            return;
        }
        if (trimmed === name) {
            setEditing(false);
            return;
        }
        onRename(trimmed).then(
            () => setEditing(false),
            () => {
                // The page shows the server's message; the editor stays open.
            },
        );
    };

    if (!editing) {
        return (
            <div className="flex items-start gap-1">
                <h1 className="min-w-0 break-words text-2xl font-bold leading-tight text-foreground sm:text-3xl">
                    {name}
                </h1>
                <button
                    type="button"
                    onClick={startEditing}
                    aria-label="Rename list"
                    className="flex size-9 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-brand-header-wash hover:text-brand-text-blue focus-visible:outline-2 focus-visible:outline-brand-header"
                >
                    <Pencil className="size-4" aria-hidden="true" />
                </button>
            </div>
        );
    }

    return (
        <form onSubmit={submit} aria-label="Rename list" className="space-y-2">
            <label htmlFor="list-name" className="block text-xs text-muted-foreground">
                List name
            </label>
            <Input
                id="list-name"
                value={draft}
                autoFocus
                maxLength={MAX_NAME_LENGTH}
                onChange={(event) => setDraft(event.target.value)}
                onKeyDown={(event) => {
                    if (event.key === 'Escape') setEditing(false);
                }}
                aria-invalid={!!error}
                aria-describedby={error ? 'list-name-error' : undefined}
                className="h-11 bg-white text-base sm:text-lg"
            />
            {error && (
                <p id="list-name-error" role="alert" className="text-sm text-red-700">
                    {error}
                </p>
            )}
            <div className="flex gap-2">
                <Button type="submit" disabled={saving} className="h-10 px-5 font-bold">
                    {saving ? 'Saving...' : 'Save'}
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setEditing(false)}
                    className="h-10 text-muted-foreground"
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
}
